import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, User, Mail, Calendar, Shield, MessageCircle, Settings as SettingsIcon } from 'lucide-react';
import dmService from '../services/dmService';
import partnerService from '../services/partnerService';
import tokenService from '../services/tokenService';
import DmPartnerSetupWizard from '../components/DmPartnerSetupWizard';
import axios from 'axios';
import url from '../url.js';
import colors from '../styles/colors';
import semanticColors from '../styles/semanticColors';
import '../styles/Settings.css';

export default function Settings() {
  const { t } = useTranslation('settings');
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    joinDate: '',
    lastLogin: ''
  });
  const [dmSettings, setDmSettings] = useState({
    dmOpen: false,
    partnerId: null,
    dmActivityId: null
  });
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showWizard, setShowWizard] = useState(false);
  const [pointsInfo, setPointsInfo] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);

  useEffect(() => {
    // Get user data from localStorage and context
    const userName = localStorage.getItem('userName') || user?.name || 'User';
    const email = localStorage.getItem('email') || localStorage.getItem('userEmail') || user?.email || '';
    const joinDate = localStorage.getItem('joinDate') || 'Unknown';
    const lastLogin = localStorage.getItem('lastLogin') || new Date().toLocaleDateString();

    setUserData({
      name: userName,
      email,
      joinDate,
      lastLogin
    });

    // Load DM settings and partners
    loadDmSettings();
    loadPartners();
    loadPoints();
  }, [user]);

  const loadDmSettings = async () => {
    try {
      const userId = user?.id || localStorage.getItem('userId');
      if (userId) {
        const settings = await dmService.getDmSettings(userId);
        setDmSettings({
          dmOpen: settings.dmOpen || false,
          partnerId: settings.partnerId || null,
          dmActivityId: settings.dmActivityId || null
        });
      }
    } catch (error) {
      console.error('Error loading DM settings:', error);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await partnerService.getAllPartners(1, 100);
      // 支援新舊兩種格式
      const partnersList = response.partners || response.data || [];
      setPartners(partnersList);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadPoints = async () => {
    setPointsLoading(true);
    try {
      const jwtToken = localStorage.getItem('jwtToken');
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};

      const response = await axios.get(
        `${url.backendHost}api/users/me/points`,
        { headers, withCredentials: true }
      );

      if (response.data.success) {
        setPointsInfo({
          balance: response.data.data.balance,
          updatedAt: response.data.data.updatedAt
        });
      }
    } catch (error) {
      console.error('Error loading points:', error);
      // 如果是 404 或 401，可能是點數系統未啟用
      if (error.response?.status === 404 || error.response?.status === 401) {
        setPointsInfo({ systemDisabled: true });
      } else {
        setPointsInfo({ error: true });
      }
    } finally {
      setPointsLoading(false);
    }
  };

  const handleDmToggle = async () => {
    try {
      setLoading(true);
      const userId = user?.id || localStorage.getItem('userId');
      if (!userId) {
        throw new Error(t('errors.getUserIdFailed'));
      }
      const newDmOpen = !dmSettings.dmOpen;
      
      const updated = await dmService.updateDmSettings(userId, {
        dmOpen: newDmOpen,
        partnerId: dmSettings.partnerId
      });

      setDmSettings({
        dmOpen: updated.dmOpen,
        partnerId: updated.partnerId,
        dmActivityId: updated.dmActivityId
      });

      setMessage({
        type: 'success',
        text: newDmOpen ? t('dm.enableSuccess') : t('dm.disableSuccess')
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating DM settings:', error);
      setMessage({
        type: 'error',
        text: t('errors.updateFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerChange = async (e) => {
    try {
      setLoading(true);
      const userId = user?.id || localStorage.getItem('userId');
      if (!userId) {
        throw new Error(t('errors.getUserIdFailed'));
      }
      const partnerId = e.target.value ? parseInt(e.target.value) : null;
      
      const updated = await dmService.updateDmSettings(userId, {
        dmOpen: dmSettings.dmOpen,
        partnerId
      });

      setDmSettings({
        dmOpen: updated.dmOpen,
        partnerId: updated.partnerId,
        dmActivityId: updated.dmActivityId
      });

      setMessage({
        type: 'success',
        text: t('dm.partnerUpdated')
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating partner:', error);
      setMessage({
        type: 'error',
        text: t('dm.partnerUpdateError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWizard = () => {
    setShowWizard(true);
  };

  const handleWizardComplete = async (partnerId) => {
    setShowWizard(false);
    
    // 重新載入 DM 設定
    await loadDmSettings();
    if (partnerId) {
      await loadPartners();
    }
    setMessage({
      type: 'success',
      text: t('dm.partnerSetupComplete')
    });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleReturnToMain = async () => {
    // 返回主頁前更新 token，確保權限正確
    try {
      await tokenService.renewToken();
    } catch (error) {
      // Token renewal failed, but continue to navigate
      console.error('Token renewal failed:', error);
    }
    navigate('/main');
  };

  const handleLogout = async () => {
    try {
      // First call backend logout
      await fetch('/auth/logout', {
        method: 'GET',
        credentials: 'include'
      });
      
      // Then call frontend logout
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still try to logout locally even if backend fails
      try {
        await logout();
        navigate('/');
      } catch (localError) {
        console.error('Local logout error:', localError);
      }
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="back-button" onClick={handleReturnToMain}>
          <ArrowLeft size={20} />
          <span>{t('backToMain')}</span>
        </button>
        <h1 className="settings-title">{t('title')}</h1>
      </div>

      <div className="settings-content">
        <div className="settings-card">
          <div className="card-header">
            <User size={24} />
            <h2>{t('personalInfo.title')}</h2>
          </div>
          
          <div className="user-info">
            <div className="user-avatar">
              <img 
                src={require('../assets/logo v4.png')} 
                alt="User Avatar" 
                className="avatar-image"
              />
            </div>
            
            <div className="user-details">
              <div className="info-item">
                <User size={18} />
                <div className="info-content">
                  <span className="info-label">{t('personalInfo.name')}</span>
                  <span className="info-value">{userData.name}</span>
                </div>
              </div>
              
              <div className="info-item">
                <Mail size={18} />
                <div className="info-content">
                  <span className="info-label">{t('personalInfo.email')}</span>
                  <span className="info-value">{userData.email}</span>
                </div>
              </div>
              
              <div className="info-item">
                <Calendar size={18} />
                <div className="info-content">
                  <span className="info-label">{t('personalInfo.joinDate')}</span>
                  <span className="info-value">{userData.joinDate}</span>
                </div>
              </div>
              
              <div className="info-item">
                <Shield size={18} />
                <div className="info-content">
                  <span className="info-label">{t('personalInfo.lastLogin')}</span>
                  <span className="info-value">{userData.lastLogin}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <span style={{ fontSize: '24px' }}>🪙</span>
            <h2>{t('points.title')}</h2>
          </div>
          
          <div className="user-info">
            {pointsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ color: semanticColors.grayText }}>{t('points.loading')}</p>
              </div>
            ) : pointsInfo?.systemDisabled ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ color: semanticColors.grayText }}>{t('points.systemDisabled')}</p>
              </div>
            ) : pointsInfo?.error ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ color: colors.error.text }}>{t('points.loadError')}</p>
              </div>
            ) : pointsInfo ? (
              <div className="user-details">
                <div className="info-item">
                  <span style={{ fontSize: '20px' }}>💰</span>
                  <div className="info-content">
                    <span className="info-label">{t('points.available')}</span>
                    <span className="info-value" style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold',
                      color: pointsInfo.balance > 0 ? colors.success.text : colors.error.text
                    }}>
                      {pointsInfo.balance} {t('points.unit')}
                    </span>
                  </div>
                </div>
                
                {pointsInfo.updatedAt && (
                  <div className="info-item">
                    <Calendar size={18} />
                    <div className="info-content">
                      <span className="info-label">{t('points.lastUpdate')}</span>
                      <span className="info-value">
                        {new Date(pointsInfo.updatedAt).toLocaleString('zh-TW')}
                      </span>
                    </div>
                  </div>
                )}
                
                <div style={{ 
                  marginTop: '16px', 
                  padding: '12px', 
                  backgroundColor: colors.info.background,
                  border: `1px solid ${colors.info.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: colors.info.text
                }}>
                  <p style={{ margin: 0 }}>
                    {t('points.description')}
                  </p>
                  <a href="https://forms.gle/b6vA4cTHhxaLXjYV7" target="_blank" rel="noopener noreferrer">{t('points.partnerLink')}</a>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <MessageCircle size={24} />
            <h2>{t('dm.title')}</h2>
          </div>
          
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="dm-settings">
            <div className="dm-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={dmSettings.dmOpen}
                  onChange={handleDmToggle}
                  disabled={loading}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {dmSettings.dmOpen ? t('dm.enabled') : t('dm.disabled')}
                </span>
              </label>
            </div>

            {dmSettings.dmOpen && (
              <div className="partner-select-wrapper">
                <div className="partner-info-section">
                  {dmSettings.partnerId ? (
                    <div className="current-partner-info">
                      <div className="partner-badge">
                        <SettingsIcon size={18} />
                        <span>{t('dm.currentPartner')}</span>
                      </div>
                      <div className="partner-details">
                        <span className="partner-name">
                          {partners.find(p => p.id === dmSettings.partnerId)?.name || t('dm.unknownPartner')}
                        </span>
                        <button 
                          className="change-partner-btn"
                          onClick={handleOpenWizard}
                          disabled={loading}
                        >
                          {t('dm.changePartner')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="no-partner-info">
                      <p className="info-message">{t('dm.noPartner')}</p>
                      <button 
                        className="setup-partner-btn"
                        onClick={handleOpenWizard}
                        disabled={loading}
                      >
                        <SettingsIcon size={18} />
                        {t('dm.setupPartner')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {dmSettings.dmActivityId && (
              <div className="dm-info">
                <p className="info-text">
                  {t('dm.activityCreated')}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="settings-actions">
          <button className="logout-button" onClick={handleLogout}>
            <LogOut size={20} />
            <span>{t('logout')}</span>
          </button>
        </div>
      </div>

      <DmPartnerSetupWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={handleWizardComplete}
        currentPartnerId={dmSettings.partnerId}
        user={user}
      />
    </div>
  );
}
